import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, InputNumber, message, Popconfirm, Select, SpinProps, Table, Tag, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { PriceBookEntry, PriceBookEntryPayload } from 'models/PriceBookEntry';
import moment from 'moment';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGetConditionsQuery } from 'redux/services/animals/animalsApi';
import { useCreatePriceBookEntryMutation, useDeletePriceBookEntryMutation, useGetProductCatalogItemsQuery, useUpdatePriceBookEntryMutation } from 'redux/services/julia/juliaApi';
import { useGetBusinessQuery } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { useAppSelector } from 'redux/store';
import { formatCurrency, formatDate } from 'utils/formatters';

const { Text } = Typography;

interface Props {
  dataSource?: PriceBookEntry[];
  loading?: SpinProps;
  isReadOnly?: boolean;
  pagination?: object;
}

const typeOptions = [
  { label: 'Standard', value: 'Standard' },
  { label: 'Promotion', value: 'Promotion' }
];

const NEW_ROW_ID = '__new__';

export const PriceBookEntriesTable = ({ dataSource, loading, isReadOnly, pagination }: Props): JSX.Element => {
  const { id: businessId } = useParams();
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);

  const { sourceProductNumber } = useAppSelector((state) => state.juliaPriceBookEntryParams);
  const { animalsConditionsParams } = useAppSelector((state) => state);
  const { data: business } = useGetBusinessQuery(businessId as string, { skip: !businessId });
  const { data: conditionsResponse } = useGetConditionsQuery(animalsConditionsParams);
  const { data: catalogResponse } = useGetProductCatalogItemsQuery({ customerId: businessId || '', params: { offset: 0, take: 500, includeInactiveData: true } }, { skip: !businessId });

  const [updateEntry] = useUpdatePriceBookEntryMutation();
  const [createEntry] = useCreatePriceBookEntryMutation();
  const [deleteEntry] = useDeletePriceBookEntryMutation();

  const conditionOptions = conditionsResponse?.data?.map((c) => ({ value: c.name, label: c.name })) || [];
  const productOptions = catalogResponse?.data?.map((item) => ({ value: item.productNumber?.trim(), label: item.productNumber?.trim() })) || [];

  const isEditing = (record: PriceBookEntry): boolean => record.id === editingId;

  const recalculateUnitPrice = (): void => {
    const listPrice = form.getFieldValue('listPrice') || 0;
    const discount = form.getFieldValue('discountPercentage') || 0;
    const unitPrice = Math.round(listPrice * (1 - discount / 100) * 100) / 100;

    form.setFieldsValue({ unitPrice });
  };

  const handleEdit = (record: PriceBookEntry): void => {
    form.setFieldsValue({
      productNumber: record.productNumber,
      condition: record.condition,
      type: record.type,
      listPrice: record.listPrice,
      unitPrice: record.unitPrice,
      discountPercentage: record.discountPercentage,
      effectiveDate: record.effectiveDate ? moment(record.effectiveDate) : null,
      endDate: record.endDate ? moment(record.endDate) : null
    });
    setEditingId(record.id);
  };

  const handleCancel = (): void => {
    setEditingId(null);
    setAddingNew(false);
  };

  const handleSave = async (recordId: string): Promise<void> => {
    try {
      const row = await form.validateFields();
      const payload: PriceBookEntryPayload = {
        productNumber: row.productNumber,
        condition: row.condition,
        type: row.type,
        listPrice: row.listPrice,
        unitPrice: row.unitPrice,
        discountPercentage: row.discountPercentage,
        effectiveDate: row.effectiveDate ? row.effectiveDate.format('YYYY-MM-DD') : '',
        endDate: row.endDate ? row.endDate.format('YYYY-MM-DD') : null
      };

      if (!business) return;

      if (addingNew) {
        await createEntry({ dataAreaId: business.dataAreaId, businessId: business.id, payload }).unwrap();
        message.success('Entry created');
        setAddingNew(false);
      } else {
        await updateEntry({ dataAreaId: business.dataAreaId, businessId: business.id, priceBookEntryId: recordId, payload }).unwrap();
        message.success('Entry saved');
      }

      setEditingId(null);
    } catch (error: any) {
      const errorMessage = typeof error?.data === 'string' ? error.data : 'Save failed';

      message.error(errorMessage, 6);
    }
  };

  const handleDelete = async (record: PriceBookEntry): Promise<void> => {
    if (!business) return;

    try {
      await deleteEntry({ dataAreaId: business.dataAreaId, businessId: business.id, priceBookEntryId: record.id }).unwrap();
      message.success('Entry deleted');
    } catch (error: any) {
      const errorMessage = typeof error?.data === 'string' ? error.data : 'Delete failed';

      message.error(errorMessage, 6);
    }
  };

  const handleAddRow = (): void => {
    if (editingId) return;

    form.setFieldsValue({
      productNumber: sourceProductNumber || undefined,
      condition: undefined,
      type: 'Standard',
      listPrice: 0,
      unitPrice: 0,
      discountPercentage: 0,
      effectiveDate: null,
      endDate: null
    });
    setEditingId(NEW_ROW_ID);
    setAddingNew(true);
  };

  const buildNewRow = (): PriceBookEntry => ({
    id: NEW_ROW_ID,
    productNumber: sourceProductNumber || '',
    condition: '',
    type: 'Standard',
    listPrice: 0,
    unitPrice: 0,
    discountPercentage: 0,
    effectiveDate: '',
    endDate: null,
    isDeleted: false,
    dataAreaId: '',
    businessId: '',
    documentType: '',
    business: { dataAreaId: '', erpId: '', id: '', name: '' },
    createdByFullName: '',
    createdByUserId: '',
    createdDateTime: '',
    modifiedByFullName: '',
    modifiedByUserId: '',
    modifiedDateTime: ''
  });

  const tableData = addingNew ? [buildNewRow(), ...(dataSource || [])] : dataSource;

  const columns: ColumnsType<PriceBookEntry> = [
    {
      title: <Text strong>Product Number</Text>,
      dataIndex: 'productNumber',
      key: 'productNumber',
      width: 180,
      fixed: 'left',
      render: (_: string, record: PriceBookEntry): React.ReactNode => {
        if (isEditing(record)) {
          return (
            <Form.Item name="productNumber" style={{ margin: 0 }} rules={[{ required: true, message: 'Required' }]}>
              <Select
                options={productOptions}
                showSearch
                disabled={!!sourceProductNumber}
                size="small"
                style={{ width: '100%' }}
                filterOption={(input, option): boolean => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          );
        }

        return <Text strong>{record.productNumber}</Text>;
      }
    },
    {
      title: <Text strong>Condition</Text>,
      dataIndex: 'condition',
      key: 'condition',
      width: 120,
      render: (_: string, record: PriceBookEntry): React.ReactNode => {
        if (isEditing(record)) {
          return (
            <Form.Item name="condition" style={{ margin: 0 }} rules={[{ required: true, message: 'Required' }]}>
              <Select
                options={conditionOptions}
                showSearch
                size="small"
                style={{ width: '100%' }}
                filterOption={(input, option): boolean => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          );
        }

        return <Tag>{record.condition}</Tag>;
      }
    },
    {
      title: <Text strong>Type</Text>,
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (_: string, record: PriceBookEntry): React.ReactNode => {
        if (isEditing(record)) {
          return (
            <Form.Item name="type" style={{ margin: 0 }} rules={[{ required: true, message: 'Required' }]}>
              <Select options={typeOptions} size="small" style={{ width: '100%' }} />
            </Form.Item>
          );
        }

        const isPromo = record.type?.toLowerCase() === 'promotion';

        return <Tag color={isPromo ? 'gold' : 'blue'}>{record.type}</Tag>;
      }
    },
    {
      title: <Text strong>List Price</Text>,
      dataIndex: 'listPrice',
      key: 'listPrice',
      width: 130,
      align: 'right',
      render: (_: number, record: PriceBookEntry): React.ReactNode => {
        if (isEditing(record)) {
          return (
            <Form.Item name="listPrice" style={{ margin: 0 }} rules={[{ required: true, message: 'Required' }]}>
              <InputNumber size="small" style={{ width: '100%' }} min={0} precision={2} prefix="$" controls={false} onChange={(): void => recalculateUnitPrice()} />
            </Form.Item>
          );
        }

        return <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(record.listPrice)}</Text>;
      }
    },
    {
      title: <Text strong>Disc %</Text>,
      dataIndex: 'discountPercentage',
      key: 'discountPercentage',
      width: 90,
      align: 'right',
      render: (_: number, record: PriceBookEntry): React.ReactNode => {
        if (isEditing(record)) {
          return (
            <Form.Item name="discountPercentage" style={{ margin: 0 }} rules={[{ required: true, message: 'Required' }]}>
              <InputNumber<number>
                size="small"
                style={{ width: '100%' }}
                min={0}
                max={100}
                precision={2}
                formatter={(value) => `${value}%`}
                parser={(value) => Number((value || '').replace('%', ''))}
                controls={false}
                onChange={(): void => recalculateUnitPrice()}
              />
            </Form.Item>
          );
        }

        return <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{record.discountPercentage}%</Text>;
      }
    },
    {
      title: <Text strong>Unit Price</Text>,
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 130,
      align: 'right',
      render: (_: number, record: PriceBookEntry): React.ReactNode => {
        if (isEditing(record)) {
          return (
            <Form.Item name="unitPrice" style={{ margin: 0 }} rules={[{ required: true, message: 'Required' }]}>
              <InputNumber size="small" style={{ width: '100%' }} min={0} precision={2} prefix="$" controls={false} />
            </Form.Item>
          );
        }

        return <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(record.unitPrice)}</Text>;
      }
    },
    {
      title: <Text strong>Effective</Text>,
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      width: 140,
      render: (_: string, record: PriceBookEntry): React.ReactNode => {
        if (isEditing(record)) {
          return (
            <Form.Item name="effectiveDate" style={{ margin: 0 }} rules={[{ required: true, message: 'Required' }]}>
              <DatePicker size="small" style={{ width: '100%' }} />
            </Form.Item>
          );
        }

        if (!record.effectiveDate) return null;

        const isActive = new Date(record.effectiveDate) <= new Date();

        return (
          <Tooltip title={record.effectiveDate}>
            <Text type={isActive ? undefined : 'secondary'}>{formatDate(record.effectiveDate)}</Text>
          </Tooltip>
        );
      }
    },
    {
      title: <Text strong>End Date</Text>,
      dataIndex: 'endDate',
      key: 'endDate',
      width: 140,
      render: (_: string | null, record: PriceBookEntry): React.ReactNode => {
        if (isEditing(record)) {
          return (
            <Form.Item name="endDate" style={{ margin: 0 }}>
              <DatePicker size="small" style={{ width: '100%' }} />
            </Form.Item>
          );
        }

        if (!record.endDate) return <Text type="secondary">No end date</Text>;

        const isExpired = new Date(record.endDate) < new Date();

        return (
          <Tooltip title={record.endDate}>
            <Text type={isExpired ? 'danger' : undefined}>{formatDate(record.endDate)}</Text>
          </Tooltip>
        );
      }
    }
  ];

  if (!isReadOnly) {
    columns.push({
      title: <Text strong>Actions</Text>,
      key: 'actions',
      width: 100,
      fixed: 'right',
      align: 'center',
      render: (_: unknown, record: PriceBookEntry): React.ReactNode => {
        if (isEditing(record)) {
          return (
            <span style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
              <Tooltip title="Save">
                <Button type="primary" size="small" icon={<CheckOutlined />} onClick={(): Promise<void> => handleSave(record.id)} />
              </Tooltip>
              <Tooltip title="Cancel">
                <Button size="small" icon={<CloseOutlined />} onClick={handleCancel} />
              </Tooltip>
            </span>
          );
        }

        return (
          <span style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
            <Tooltip title="Edit">
              <Button size="small" icon={<EditOutlined />} disabled={editingId !== null} onClick={(): void => handleEdit(record)} />
            </Tooltip>
            <Popconfirm title="Delete this entry?" onConfirm={(): Promise<void> => handleDelete(record)} okText="Delete" okButtonProps={{ danger: true }}>
              <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} disabled={editingId !== null} />
              </Tooltip>
            </Popconfirm>
          </span>
        );
      }
    });
  }

  return (
    <Form form={form} component={false}>
      {!isReadOnly && (
        <div style={{ marginBottom: 12 }}>
          <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddRow} disabled={editingId !== null}>
            Add Entry
          </Button>
        </div>
      )}
      <Table
        columns={columns}
        dataSource={tableData}
        size="small"
        bordered
        loading={loading}
        rowKey={(item): string => item.id}
        scroll={{ x: 'max-content' }}
        pagination={pagination}
        rowClassName={(record, index): string => {
          const classes: string[] = [];

          if (record.id === NEW_ROW_ID) classes.push('price-book-row-new');
          if (index % 2 === 1) classes.push('price-book-row-striped');
          if (record.endDate && new Date(record.endDate) < new Date()) classes.push('price-book-row-expired');
          if (record.type?.toLowerCase() === 'promotion') classes.push('price-book-row-promotion');

          return classes.join(' ');
        }}
      />
    </Form>
  );
};
